import { type Component, createMemo, createSignal, onCleanup, onMount, Show } from 'solid-js'
import { Portal } from 'solid-js/web'
import { twMerge } from 'tailwind-merge'
import type { TooltipPlacementType, TooltipPropsType } from '~/types/Tooltip'
import { getGsap, MOTION, prefersReducedMotion } from '~/utils/gsap'

const POSITION_ORDER: readonly TooltipPlacementType[] = ['bottom', 'top', 'right', 'left']
const SPACE_MARGIN = 8
const HIDE_DELAY = 160
const MAX_RETRIES = 5

const ORIGIN_BY_PLACEMENT: Record<string, string> = {
  top: 'center bottom',
  bottom: 'center top',
  left: 'right center',
  right: 'left center'
}

const Tooltip: Component<TooltipPropsType> = props => {
  const [isVisible, setIsVisible] = createSignal(false)
  const [isPositioned, setIsPositioned] = createSignal(false)
  const [tooltipTop, setTooltipTop] = createSignal(0)
  const [tooltipLeft, setTooltipLeft] = createSignal(0)
  const [currentPlacement, setCurrentPlacement] = createSignal<TooltipPlacementType>(props.placement || 'auto')

  let triggerRef: HTMLDivElement | undefined
  let tooltipRef: HTMLDivElement | undefined
  let rafId: number | undefined
  let retryCount = 0
  let scrollParents: HTMLElement[] = []
  let resizeObserver: ResizeObserver | undefined
  let enterAnim: { kill: () => void } | undefined
  let didAnimateIn = false

  const duration = () => (props.duration ?? HIDE_DELAY) / 1000

  const calculateBestPlacement = (): TooltipPlacementType => {
    if (!triggerRef || !tooltipRef) return 'top'

    const triggerRect = triggerRef.getBoundingClientRect()
    const tooltipRect = tooltipRef.getBoundingClientRect()

    const spaceByPlacement: Record<string, number> = {
      bottom: window.innerHeight - triggerRect.bottom,
      top: triggerRect.top,
      right: window.innerWidth - triggerRect.right,
      left: triggerRect.left
    }

    const minSpace = tooltipRect.height + SPACE_MARGIN
    for (const pos of POSITION_ORDER) {
      const available = spaceByPlacement[pos]
      if ((pos === 'bottom' || pos === 'top') && available >= minSpace) return pos
      if ((pos === 'left' || pos === 'right') && available >= tooltipRect.width + SPACE_MARGIN) return pos
    }

    const sorted = POSITION_ORDER.map(p => ({ placement: p, space: spaceByPlacement[p] })).sort((a, b) => b.space - a.space)
    return sorted[0].placement
  }

  const animateIn = () => {
    if (didAnimateIn || !tooltipRef) return
    didAnimateIn = true
    const g = getGsap()
    const t = props.transition ?? 'zoom'
    const placement = currentPlacement()
    const origin = ORIGIN_BY_PLACEMENT[placement as string] ?? 'center center'
    if (!g || prefersReducedMotion() || t === 'fade') {
      if (g && !prefersReducedMotion()) {
        enterAnim = g.fromTo(tooltipRef, { autoAlpha: 0 }, { autoAlpha: 1, duration: duration(), ease: MOTION.ease })
        return
      }
      tooltipRef.style.visibility = 'visible'
      tooltipRef.style.opacity = '1'
      return
    }
    const fromScale = t === 'grow' ? 0.95 : 0.9
    enterAnim = g.fromTo(tooltipRef, { autoAlpha: 0, scale: fromScale, transformOrigin: origin }, { autoAlpha: 1, scale: 1, duration: duration(), ease: MOTION.ease })
  }

  const updatePosition = () => {
    if (!triggerRef || !tooltipRef || props.placement === 'none') return

    if (rafId) cancelAnimationFrame(rafId)
    rafId = requestAnimationFrame(() => {
      if (!triggerRef || !tooltipRef) return

      const triggerRect = triggerRef.getBoundingClientRect()
      const tooltipRect = tooltipRef.getBoundingClientRect()

      if (tooltipRect.width === 0 || tooltipRect.height === 0) {
        if (retryCount < MAX_RETRIES) {
          retryCount++
          rafId = requestAnimationFrame(updatePosition)
          return
        }
      }
      retryCount = 0

      const finalPlacement = props.placement === 'auto' || !props.placement ? calculateBestPlacement() : props.placement
      setCurrentPlacement(finalPlacement)

      let top = 0
      let left = 0

      if (finalPlacement === 'top') {
        top = triggerRect.top - tooltipRect.height - SPACE_MARGIN
        left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2
      } else if (finalPlacement === 'bottom') {
        top = triggerRect.bottom + SPACE_MARGIN
        left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2
      } else if (finalPlacement === 'left') {
        top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2
        left = triggerRect.left - tooltipRect.width - SPACE_MARGIN
      } else {
        top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2
        left = triggerRect.right + SPACE_MARGIN
      }

      const vw = window.innerWidth
      const vh = window.innerHeight
      left = Math.max(SPACE_MARGIN, Math.min(left, vw - tooltipRect.width - SPACE_MARGIN))
      top = Math.max(SPACE_MARGIN, Math.min(top, vh - tooltipRect.height - SPACE_MARGIN))

      setTooltipTop(top)
      setTooltipLeft(left)
      setIsPositioned(true)
      rafId = undefined
      // Ya posicionado: animamos la entrada (una sola vez por aparición).
      animateIn()
    })
  }

  const getScrollParents = (el: HTMLElement | null): HTMLElement[] => {
    const parents: HTMLElement[] = []
    let node = el?.parentElement ?? null
    while (node && node !== document.body) {
      const { overflowY, overflowX } = getComputedStyle(node)
      if (/(auto|scroll|overlay)/.test(overflowY + overflowX)) parents.push(node)
      node = node.parentElement
    }
    parents.push((document.scrollingElement as HTMLElement) || document.documentElement)
    return parents
  }

  const showTooltip = () => {
    if (props.placement === 'none') return
    enterAnim?.kill()
    retryCount = 0
    didAnimateIn = false
    setIsPositioned(false)
    setIsVisible(true)
    requestAnimationFrame(updatePosition)
  }

  const hideTooltip = () => {
    if (!isVisible()) return
    const finish = () => {
      setIsVisible(false)
      setIsPositioned(false)
    }
    const g = getGsap()
    enterAnim?.kill()
    if (!g || prefersReducedMotion() || !tooltipRef) {
      finish()
      return
    }
    const t = props.transition ?? 'zoom'
    g.to(tooltipRef, {
      autoAlpha: 0,
      scale: t === 'fade' ? 1 : t === 'grow' ? 0.95 : 0.9,
      duration: duration(),
      ease: MOTION.ease,
      onComplete: finish
    })
  }

  onMount(() => {
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, { passive: true })
    scrollParents = getScrollParents(triggerRef || null)
    scrollParents.forEach(p => p.addEventListener('scroll', updatePosition, { passive: true }))
    if (window.ResizeObserver && triggerRef) {
      resizeObserver = new ResizeObserver(() => updatePosition())
      resizeObserver.observe(triggerRef)
    }
  })

  onCleanup(() => {
    window.removeEventListener('resize', updatePosition)
    window.removeEventListener('scroll', updatePosition)
    scrollParents.forEach(p => p.removeEventListener('scroll', updatePosition))
    resizeObserver?.disconnect()
    enterAnim?.kill()
    if (rafId) cancelAnimationFrame(rafId)
  })

  const arrowClassMemo = createMemo(() => {
    const base = 'absolute w-2 h-2 rotate-45 bg-slate-900 pointer-events-none'
    const placement = currentPlacement()
    if (placement === 'top') return `${base} -bottom-1 left-1/2 -translate-x-1/2`
    if (placement === 'bottom') return `${base} -top-1 left-1/2 -translate-x-1/2`
    if (placement === 'left') return `${base} -right-1 top-1/2 -translate-y-1/2`
    if (placement === 'right') return `${base} -left-1 top-1/2 -translate-y-1/2`
    return ''
  })

  return (
    <>
      <div
        ref={triggerRef}
        class={twMerge('inline-block w-min whitespace-nowrap', props.triggerClass || '')}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
      >
        {props.children}
      </div>
      <Show when={isVisible() && props.placement !== 'none'}>
        <Portal>
          <div
            ref={tooltipRef}
            class={twMerge('fixed z-[1200] px-2 py-1 text-xs font-medium text-white bg-slate-900 rounded shadow-sm pointer-events-none will-change-transform', props.class || '')}
            style={{
              top: `${tooltipTop()}px`,
              left: `${tooltipLeft()}px`,
              opacity: 0,
              visibility: isPositioned() ? 'visible' : 'hidden'
            }}
            data-placement={currentPlacement()}
          >
            {props.content}
            <div class={arrowClassMemo()} />
          </div>
        </Portal>
      </Show>
    </>
  )
}

export default Tooltip
